import React, { useState, useEffect } from 'react';
import { Button, Modal, Typography, Space, Alert, Switch } from 'antd';
import { BellOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import pushNotificationService from '../../services/pushNotificationService';

const { Title, Text } = Typography;

const PushNotificationPrompt = ({ visible, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState('idle'); // idle, success, error, denied
    const [error, setError] = useState('');
    const [isEnabled, setIsEnabled] = useState(false);

    useEffect(() => {
        if (visible) {
            checkNotificationStatus();
        }
    }, [visible]);

    const checkNotificationStatus = () => {
        const isSupported = pushNotificationService.isSupported;
        const isEnabled = pushNotificationService.isEnabled();
        
        setIsEnabled(isEnabled);
        
        if (!isSupported) {
            setStatus('error');
            setError('Push notifications are not supported in this browser. Please use a modern browser like Chrome, Firefox, or Edge.');
        } else if (isEnabled) {
            setStatus('success');
        } else {
            setStatus('idle');
        }
    };

    const handleEnableNotifications = async () => {
        setLoading(true);
        setError('');

        try {
            const success = await pushNotificationService.initialize();
            
            if (success) {
                setStatus('success');
                setIsEnabled(true);
                onSuccess && onSuccess();
            } else {
                setStatus('error');
                setError('Failed to enable push notifications. Please try again.');
            }
        } catch (err) {
            setStatus('error');
            setError(err.message || 'Failed to enable push notifications. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleTestNotification = async () => {
        setLoading(true);
        try {
            await pushNotificationService.testNotification();
        } catch (err) {
            setError('Failed to send test notification: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const getStatusIcon = () => {
        switch (status) {
            case 'success':
                return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '24px' }} />;
            case 'error':
                return <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '24px' }} />;
            default:
                return <BellOutlined style={{ color: '#1890ff', fontSize: '24px' }} />;
        }
    };

    const getStatusText = () => {
        switch (status) {
            case 'success':
                return 'Push notifications are enabled!';
            case 'error':
                return 'Failed to enable push notifications';
            default:
                return 'Enable push notifications to stay updated';
        }
    };

    const getStatusDescription = () => {
        switch (status) {
            case 'success':
                return 'You will now receive push notifications for new messages, quotes, and booking updates.';
            case 'error':
                return error;
            default:
                return 'Get instant notifications for new messages, quotes, booking updates, and more.';
        }
    };

    return (
        <Modal
            title={
                <Space>
                    {getStatusIcon()}
                    <span>Push Notifications</span>
                </Space>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={500}
            centered
        >
            <div className="text-center py-4">
                <Title level={4} className="mb-2">
                    {getStatusText()}
                </Title>
                
                <Text className="text-gray-600 mb-6 block">
                    {getStatusDescription()}
                </Text>

                {status === 'error' && (
                    <Alert
                        message="Error"
                        description={error}
                        type="error"
                        showIcon
                        className="mb-4 text-left"
                    />
                )}

                {status === 'success' && (
                    <Alert
                        message="Success"
                        description="Push notifications are now enabled. You can test them below."
                        type="success"
                        showIcon
                        className="mb-4"
                    />
                )}

                <Space direction="vertical" className="w-full">
                    {status === 'idle' && (
                        <Button
                            type="primary"
                            size="large"
                            icon={<BellOutlined />}
                            loading={loading}
                            onClick={handleEnableNotifications}
                            className="w-full"
                        >
                            Enable Push Notifications
                        </Button>
                    )}

                    {status === 'success' && (
                        <Button
                            type="default"
                            size="large"
                            icon={<BellOutlined />}
                            loading={loading}
                            onClick={handleTestNotification}
                            className="w-full"
                        >
                            Send Test Notification
                        </Button>
                    )}

                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                            <Text strong>Push Notifications</Text>
                            <br />
                            <Text type="secondary" className="text-sm">
                                {isEnabled ? 'Enabled' : 'Disabled'}
                            </Text>
                        </div>
                        <Switch
                            checked={isEnabled}
                            disabled={!pushNotificationService.isSupported}
                            onChange={(checked) => {
                                if (checked && !isEnabled) {
                                    handleEnableNotifications();
                                }
                            }}
                        />
                    </div>

                    <Text type="secondary" className="text-xs">
                        You can change this setting anytime in your browser's notification preferences.
                    </Text>
                </Space>
            </div>
        </Modal>
    );
};

export default PushNotificationPrompt;
